import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
  viewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';

import { ScreenFieldModel } from '@models/module/fields/main';
import { DataListViewAction } from '@models/module/main/dataListViewAction';
import {
  CalendarDialogDocDetailComponent,
  CalendarDialogDocDetailOuterHeight,
  CalendarDialogDocDetailOuterWidth,
  CalendarDialogDocDetailPrefix,
  CalendarDialogDocDetailWideOuterHeight,
  CalendarDialogDocDetailWideOuterWidth,
  hasTableOrWeatherField,
} from '@module-instance/calendar/calendar-dialog-doc-detail/calendar-dialog-doc-detail.component';
import { CalendarDialogMorePrefix } from '@module-instance/calendar/calendar-dialog-more/calendar-dialog-more.component';
import { CalendarTileDoc } from '@module-instance/calendar/calendar.model';

const TABLE_PREVIEW_POSITIONS: ConnectedPosition[] = [
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 2 },
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -2 },
];

@Component({
  selector: 'app-calendar-doc-mini',
  templateUrl: './calendar-doc-mini.component.html',
  styleUrls: ['./calendar-doc-mini.component.scss'],
  imports: [TranslateModule],
})
export class CalendarDocMiniComponent implements OnDestroy {
  private readonly dialog = inject(MatDialog);
  private readonly ref = inject(ElementRef);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);

  readonly doc = input<CalendarTileDoc>();
  readonly screenFields = input<ScreenFieldModel[]>();
  readonly actions = input<DataListViewAction[]>();
  readonly tablePreviewTpl = viewChild.required<TemplateRef<unknown>>('tablePreviewTpl');

  private previewOverlayRef: OverlayRef;
  private closePreviewTimeout: ReturnType<typeof setTimeout>;

  ngOnDestroy(): void {
    this.destroyTablePreview();
  }

  /** Attached via CDK overlay (not a plain CSS :hover child), so it can escape the day tile's
   *  `overflow: hidden` and visually span over neighboring days. */
  showTablePreview(): void {
    if (!this.doc().descTableRows?.length) {
      return;
    }
    clearTimeout(this.closePreviewTimeout);
    if (this.previewOverlayRef) {
      return;
    }

    this.previewOverlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.ref)
        .withPositions(TABLE_PREVIEW_POSITIONS)
        .withPush(true),
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });
    this.previewOverlayRef.attach(new TemplatePortal(this.tablePreviewTpl(), this.viewContainerRef));

    const overlayElement = this.previewOverlayRef.overlayElement;
    overlayElement.addEventListener('mouseenter', this.cancelClosePreview);
    overlayElement.addEventListener('mouseleave', this.scheduleClosePreview);
  }

  /** Delayed so moving the mouse from the trigger into the (separately positioned) popup doesn't close it. */
  scheduleClosePreview = (): void => {
    clearTimeout(this.closePreviewTimeout);
    this.closePreviewTimeout = setTimeout(() => this.destroyTablePreview(), 150);
  };

  private cancelClosePreview = (): void => {
    clearTimeout(this.closePreviewTimeout);
  };

  private destroyTablePreview(): void {
    clearTimeout(this.closePreviewTimeout);
    if (this.previewOverlayRef) {
      this.previewOverlayRef.dispose();
      this.previewOverlayRef = undefined;
    }
  }

  openDoc(event: any) {
    // check if this doc mini is inside a dialog
    const dialogMoreId = event?.target?.closest('.mat-dialog-container')?.id;

    // do not open the same dialog again
    const dialogDocDetailId = this.getDialogId();
    const sameDocDetailDialog = this.dialog.openDialogs.find(d => d.id && d.id == dialogDocDetailId);
    if (sameDocDetailDialog) {
      return;
    }

    // find and close all doc detail dialogs, we will keep only one doc detail dialog open
    const differentDocDetailDialogs = this.dialog.openDialogs.filter(
      d => d.id && d.id.startsWith(CalendarDialogDocDetailPrefix),
    );
    for (const dialog of differentDocDetailDialogs) {
      dialog.close();
    }

    // find and close all more dialogs different than the parent dialog
    const differentMoreDialogs = this.dialog.openDialogs.filter(
      d => d.id && d.id.startsWith(CalendarDialogMorePrefix) && d.id != dialogMoreId,
    );
    for (const dialog of differentMoreDialogs) {
      dialog.close();
    }

    // open this doc detail dialog
    this.openDialog();
  }

  getDialogId() {
    return `${CalendarDialogDocDetailPrefix}-${moment(this.doc().ref.date).format('YYYY-MM-DD')}-${
      this.doc().ref.full_doc_num
    }`;
  }

  openDialog() {
    const docRect: DOMRect = this.ref.nativeElement.getBoundingClientRect();
    const parentRect: DOMRect = document.body.getBoundingClientRect();

    const isWide = hasTableOrWeatherField(this.screenFields());
    const width = isWide ? CalendarDialogDocDetailWideOuterWidth : CalendarDialogDocDetailOuterWidth,
      height = isWide ? CalendarDialogDocDetailWideOuterHeight : CalendarDialogDocDetailOuterHeight,
      margin = 8;
    let top = docRect.top;
    let left = docRect.left - width - margin;

    if (top + height > parentRect.height) {
      top = parentRect.bottom - height - margin;
    }
    if (left < parentRect.left) {
      left = docRect.right + margin;
    }
    if (left + width > parentRect.right) {
      left = parentRect.right - width - margin;
    }
    if (left < 0) {
      left = margin;
    }

    this.dialog.open(CalendarDialogDocDetailComponent, {
      data: {
        doc: this.doc(),
        screenFields: this.screenFields(),
        actions: this.actions(),
      },
      hasBackdrop: false,
      id: this.getDialogId(),
      position: {
        top: `${top}px`,
        left: `${left}px`,
      },
      width: `${width}px`,
      maxWidth: `calc(100vw - ${margin * 2}px)`,
    });
  }
}
